import { useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from 'react';
import { Formik, Form } from 'formik';
import Axios from 'axios';
import * as Yup from 'yup';
import ErrorMessages from '../../errorConfig';
import PatientRegistrationComponent from './PatientRegistrationComponent';
import PmsUrlConfig from '../../urlConfig';
import pmsConstants from '../../pmsConstants';
import { emailRegex, validateUsername } from '../common';
import PatientDetails from './PatientDetailsComponent';
import { Link } from 'react-router-dom';
import {useDispatch} from 'react-redux';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import AgreementComponent from './AgreementComponent';

const AddUserSchema = Yup.object().shape({
    firstname: Yup.string().required(ErrorMessages.FIRSTNAME_REQUIRED),
    lastname: Yup.string().required(ErrorMessages.LASTNAME_REQUIRED),
    userEmail: Yup.string()
        .required(ErrorMessages.EMAIL_REQUIRED)
        .matches(emailRegex, ErrorMessages.EMAIL_INVALID),
    password: Yup.string()
        .required(ErrorMessages.PASSWORD_REQUIRED)
        .min(8, ErrorMessages.PASSWORD_LENGTH),
    confPassword: Yup.string()
        .oneOf([Yup.ref('password')], ErrorMessages.PASSWORDS_DONT_MATCH),
    phoneNo: Yup.string().required(ErrorMessages.PHONE_NUM_REQUIRED),
    patientZipcode: Yup.string()
    .required(ErrorMessages.ZIPCODE_REQUIRED)    
    .matches(/^[0-9]+$/, "Must be only digits")
        .min(5, "Must be exactly 5 digits")
        .max(5, "Must be exactly 5 digits"),
    gender: Yup.string().required(ErrorMessages.GENDER_REQUIRED),
});

export default function AddPatient() {


    const navigate = useNavigate();
    const dispatch = useDispatch();
    // const [serverErr, setServerError] = useState('');
    const [shouldResetForm, setShouldResetForm] = useState(false);
    const [agreementDocumentUrl, setAgreementDocumentUrl] = useState('');
    const [showAgreement, setShowAgreement] = useState(false);
    const [pendingRegistration, setPendingRegistration] = useState(null);
    const [isRegistering, setIsRegistering] = useState(false);
    const agreementRequestInProgress = useRef(false);
    const registrationInProgress = useRef(false);
    
    useEffect(() => {
        if (shouldResetForm) {
            clearInputFields();
            setShouldResetForm(false);
        }
    }, [shouldResetForm]);

    const clearInputFields = () => {
        document.getElementById("add-patient-form").reset();
    }

    const handleAgreementCancel = () => {
        setShowAgreement(false);
        setAgreementDocumentUrl('');
        setPendingRegistration(null);
    };

    const handleAgreementAccept = async (agreement) => {
        if (registrationInProgress.current || !pendingRegistration || !agreement.accepted) return;

        registrationInProgress.current = true;
        setIsRegistering(true);
        setShowAgreement(false);

        const values = pendingRegistration;
        let dateOfBirthToSubmit = values.dateOfBirth;
        if (dateOfBirthToSubmit) {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const dobDate = new Date(dateOfBirthToSubmit);
            dobDate.setHours(0, 0, 0, 0);
            if (dobDate.getTime() === today.getTime()) {
                dateOfBirthToSubmit = null;
            }
        }

        try {
            const response = await Axios.post(PmsUrlConfig.BOS_ADD_PATIENT_URL, {
                firstname: values.firstname,
                lastname: values.lastname,
                gender: values.gender,
                dateOfBirth: dateOfBirthToSubmit,
                age: dateOfBirthToSubmit ? parseInt(values.age) : null,
                email: values.userEmail,
                phoneNum: values.phoneNo.replace(/[^\d]/g, ''),
                password: values.password,
                password_confirm: values.password,
                user_type: values.user_type,
                active_user: false,
                zip_code: values.patientZipcode,
                search_radius_miles: parseInt(values.patientSearchRadius),
                insurance: values.patientInsurance,
                user_agreements: [{
                    accepted_date: agreement.acceptedDate,
                    accepted_agreement: agreement.acceptedAgreement
                }]
            });

            if (response.status === 200) {
                dispatch({ type: 'clearError' });
                const patientUser = response.data.email;
                toast.success(`User ${patientUser} registered successfully!`, {
                    position: "bottom-center",
                    autoClose: 3000,
                    hideProgressBar: true,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true
                });
                setShouldResetForm(true);
                setTimeout(() => navigate(PmsUrlConfig.LOGIN_URL), 3000);
            } else {
                toast.error(ErrorMessages.SERVER_ERROR, {
                    position: "bottom-center",
                    autoClose: 5000,
                    hideProgressBar: true,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true
                });
            }
        } catch (error) {
            console.log('Error caught:', error);
            console.log('Error response:', error.response);
            console.log('Error status:', error.response?.status);
            console.log('Error data:', error.response?.data);

            if (error.response?.data?.error_code?.description) {
                toast.error(error.response.data.error_code.description, {
                    position: "bottom-center",
                    autoClose: 5000,
                    hideProgressBar: true,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true
                });
            } else {
                toast.error(ErrorMessages.SERVER_ERROR, {
                    position: "bottom-center",
                    autoClose: 5000,
                    hideProgressBar: true,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true
                });
            }
        } finally {
            registrationInProgress.current = false;
            setIsRegistering(false);
            setPendingRegistration(null);
            setAgreementDocumentUrl('');
        }
    };

    return (
        <div className="max-w-5xl mx-auto p-4">
            <Formik
                initialValues={{
                    firstname: '',
                    lastname: '',
                    gender: '',
                    dateOfBirth: new Date(),
                    age: '',
                    userEmail: '',
                    phoneNo: '',
                    password: '',
                    confPassword: '',
                    user_type: pmsConstants.USER_TYPE_PATIENT,
                    patientZipcode: '',
                    patientSearchRadius: '5',
                    patientInsurance: ''
                }}
                validationSchema={AddUserSchema}
                onSubmit={async (values, { setSubmitting }) => {
                    if (agreementRequestInProgress.current || registrationInProgress.current) {
                        setSubmitting(false);
                        return;
                    }

                    // same shape as initial values 
                    var isValidUsername = validateUsername(values.userEmail,)
                    if (!isValidUsername) {
                        toast.error("Username already exists!", {
                            position: "bottom-center",
                            autoClose: 5000,
                            hideProgressBar: true,
                            closeOnClick: true,
                            pauseOnHover: true,
                            draggable: true
                        });
                        setSubmitting(false);
                        return;
                    }

                    agreementRequestInProgress.current = true;
                    try {
                        const response = await Axios.get(PmsUrlConfig.BOS_GET_PATIENT_AGREEMENT_URL);
                        const documentUrl = getPatientAgreementDocumentUrl(response.data);
                        if (!isHttpUrl(documentUrl)) {
                            throw new Error('Invalid patient agreement document URL');
                        }

                        setPendingRegistration({ ...values });
                        setAgreementDocumentUrl(documentUrl);
                        setShowAgreement(true);
                    } catch (error) {
                        console.log('Failed to load patient agreement:', error);
                        const errorMessage = error.response?.data?.error_code?.description || 'Unable to load the patient agreement. Please try again.';
                        toast.error(errorMessage, {
                            position: "bottom-center",
                            autoClose: 5000,
                            hideProgressBar: true,
                            closeOnClick: true,
                            pauseOnHover: true,
                            draggable: true
                        });
                    } finally {
                        agreementRequestInProgress.current = false;
                        setSubmitting(false);
                    }
                }}
                onReset={(values) => {
                    clearInputFields();

                }}
            >
                {({ errors, touched, values, handleChange, isSubmitting, handleReset, handleBlur, setFieldValue }) => (
                    <Form data-testid='password-form' id="add-patient-form">
                        <div className="w-full max-w-7xl mx-auto p-4 sm:p-6 lg:p-8">
                            <div className="mb-4">
                                <div className="bg-white shadow-md rounded-lg">
                                    <div className="px-6 py-4 border-b border-gray-200">
                                        <h3 className="text-lg font-semibold">Patient Registration</h3>
                                    </div>
                                    <div className="p-6">
                                        <PatientRegistrationComponent
                                            errors={errors}
                                            touched={touched}
                                            values={values}
                                            handleBlur={handleBlur}
                                            handleChange={handleChange}
                                            prefix={'patientSignup'}
                                            editPage={false}
                                            patientRegistration={true}
                                            setFieldValue={setFieldValue}
                                            handleReset={handleReset}
                                        />
                                    </div>
                                    <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
                                        <PatientDetails
                                            errors={errors}
                                            touched={touched}
                                            values={values}
                                            handleBlur={handleBlur}
                                            handleChange={handleChange}
                                            prefix={'patient'}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="flex flex-col space-y-4">
                                <div className="flex space-x-4">
                                    <button
                                        className="w-32 bg-teal-600 hover:bg-teal-700 text-white font-bold py-2 px-4 rounded disabled:opacity-50"
                                        type="submit"
                                        disabled={isSubmitting || isRegistering || showAgreement}
                                    >
                                        {isSubmitting ? "Loading agreement..." : isRegistering ? "Registering..." : "Register"}
                                    </button>
                                    <button
                                        className="w-32 bg-gray-100 border border-teal-300 hover:bg-gray-200 text-teal-600 font-bold py-2 px-4 rounded"
                                        type="button"
                                        onClick={handleReset}
                                    >
                                        Reset
                                    </button>
                                </div>
                                <div className="text-center">
                                    <p className="text-gray-600">
                                        Already have an account?{' '}
                                        <Link to={`${PmsUrlConfig.HOME_URL}`} className="text-blue-500 hover:text-blue-700">Login</Link>
                                    </p>
                                </div>
                            </div>
                        </div>
                    </Form>
                )}
            </Formik>
            {showAgreement && (
                <AgreementComponent
                    agreementType="Patient"
                    documentUrl={agreementDocumentUrl}
                    onAccept={handleAgreementAccept}
                    onCancel={handleAgreementCancel}
                />
            )}
        </div>
    );


}

function getPatientAgreementDocumentUrl(responseData) {
    let agreementData = responseData;
    if (typeof agreementData === 'string') {
        try {
            agreementData = JSON.parse(agreementData);
        } catch {
            return '';
        }
    }

    return typeof agreementData === 'object' && agreementData !== null
        ? agreementData.url
        : '';
}

function isHttpUrl(value) {
    if (typeof value !== 'string') return false;
    try {
        const url = new URL(value);
        return url.protocol === 'http:' || url.protocol === 'https:';
    } catch {
        return false;
    }
}